import { css } from '@emotion/core'
import dayjs from 'dayjs'
import { graphql } from 'gatsby'
import { GatsbyImage } from 'gatsby-plugin-image'
import React from 'react'

import Block from '../components/Block'
import Body from '../components/Body'
import { CardsNavigation } from '../components/CardsNavigation'
import { BlogAuthorInfo } from '../components/content-management/blog/BlogAuthorInfo'
import { BlogPostCard } from '../components/content-management/blog/BlogPostCard'
import CTANewsletter from '../components/ctas/CTANewsletter'
import Divider from '../components/Divider'
import Footer from '../components/Footer'
import Header from '../components/Header'
import { IntroHeroBlock } from '../components/IntroHeroBlock'
import Layout from '../components/Layout'
import { Link } from '../components/Link'
import ScrollToTop from '../components/ScrollToTop'
import { SearchResultsBlock } from '../components/Search'
import SEO from '../components/SEO'
import VerticalTextDivider from '../components/VerticalTextDivider'
import { breakpoints, headerWithSearchBreakPoint } from '../constants/breakPoints'
import { blogBreakPoint } from '../constants/breakPoints'
import { getMappedBlogPosts } from '../helpers/blog'
import { isMobile } from '../helpers/common'
import { getSearchHeaderEntriesLogin } from '../helpers/header'
import { useDebouncedValue } from '../hooks/useDebouncedValue'
import { useHsFormCallback } from '../hooks/useHsFormCallback'
import { useLocalStorage } from '../hooks/useLocalStorage'
import { BlogPageProps } from '../interfaces/blog'
import { HeaderProps } from '../interfaces/header'

export const BlogHeader: React.FunctionComponent<HeaderProps> = (props) => {
  return (
    <Header
      entriesLogin={getSearchHeaderEntriesLogin}
      customBreakPoint={headerWithSearchBreakPoint}
      searchPlaceholder={'Search Blog...'}
      {...props}
    />
  )
}

const BlogPage: React.FunctionComponent<BlogPageProps> = ({
  data: {
    blogPosts: allBlogPosts,
    popularPosts: allPopularPosts,
    blogCategories: { edges: categories },
  },
}) => {
  const blogPosts = getMappedBlogPosts(allBlogPosts)
  const firstPost = blogPosts[0]
  const [uploadedPosts, setUploadedPosts] = React.useState(blogPosts.slice(1, 9))
  const popularPosts = getMappedBlogPosts(allPopularPosts, false)

  const [search, setSearch] = React.useState('')
  const debouncedSearch = useDebouncedValue(search)

  const filteredSearchPosts = React.useMemo(
    () => blogPosts.filter(({ title }) => title.toLowerCase().includes(debouncedSearch.toLowerCase())),
    [debouncedSearch]
  )

  React.useEffect(() => {
    const scrollHandler = () => {
      if (window.scrollY > document.getElementById('last-posts-block')!.offsetHeight - 300) {
        setUploadedPosts((uploadedPosts) => [
          ...uploadedPosts,
          ...blogPosts.slice(uploadedPosts.length + 1, uploadedPosts.length + 17),
        ])
      }
    }

    document.addEventListener('scroll', scrollHandler)

    return () => {
      document.removeEventListener('scroll', scrollHandler)
    }
  }, [])

  //  initial scroll on page load
  React.useEffect(() => {
    window.scrollTo({ top: 0 })
  }, [])

  const setBlogSubscribed = useLocalStorage('blog_subscribed', false)[1]

  useHsFormCallback({
    onFormSubmittedCallback: () => setBlogSubscribed(true),
    hubspotFormIds: ['afc63de9-b452-4473-b3c3-f1f8df035204', '7cd8c2f7-7dc1-498c-9184-29539d228229'],
  })

  return (
    <Layout>
      <SEO
        title={'Blog - Get Your Product Strategy Right'}
        description={
          'The airfocus blog is the top hub to get educational material, tips, and inspiration to help you manage your product strategy. Learn from product management experts.'
        }
      />
      <BlogHeader search={search} setSearch={setSearch} />
      <Body>
        <IntroHeroBlock
          headlineText={'Get your product strategy right.'}
          subheaderText={
            'The airfocus blog is the top hub to get educational material, tips, and inspiration to help you manage your product strategy.'
          }
          withoutCTA
          withoutBorder
          withoutMarginBottom
          cssStyles={introHeroStyle}
        />

        <ScrollToTop referenceIdForVisibility={'last-posts-block'} />

        <Block
          columns={2}
          columnGap={2}
          breakPoint={blogBreakPoint}
          columnsTemplate={'1fr 1.5fr'}
          css={topContainerStyle}
        >
          <div css={firstPostDataStyle}>
            <div className={'strong-body-2'}>
              <Link
                className={'opacity-on-hover'}
                href={`/blog/category/${firstPost.postCategory.slug}/`}
                css={firstPostLinkStyle(firstPost.postCategory.categoryColor)}
              >
                {firstPost.postCategory.name}
              </Link>
              <span className={'gray-text'}>
                <VerticalTextDivider /> {dayjs(firstPost.publishedAt || firstPost.createdAt).format('D MMM YYYY')}
              </span>
            </div>
            <Link href={`/blog/${firstPost.slug}/`}>
              <h2 className={'heading2 black-text'} css={headingStyle}>
                {firstPost.title}
              </h2>
            </Link>
            <Link className={'subtitle black-text'} href={`/blog/${firstPost.slug}/`} css={abstractStyle}>
              {firstPost.abstract}
            </Link>
            <BlogAuthorInfo author={firstPost.postAuthor} />
          </div>
          <Link href={`/blog/${firstPost.slug}/`} css={bigImageStyle}>
            <GatsbyImage image={firstPost.heroImage.gatsbyImageData} alt={firstPost.title} loading={'eager'} />
          </Link>
        </Block>

        <CardsNavigation
          categories={categories}
          heading={'Read by category'}
          columns={4}
          isHorisontal
          hrefPrefix={'blog/category'}
        />

        <Block
          columns={2}
          responsiveReversed
          breakPoint={blogBreakPoint}
          alignContent={'flex-start'}
          columnsTemplate={'2fr 1fr'}
          css={bottomContainerStyle}
        >
          <div id={'last-posts-block'}>
            <Block columns={2} columnGap={1.5} twoColumnsForTablet rowGapMobile={2} alignContent={'stretch'}>
              <BlogPostCard key={uploadedPosts[0].id} {...uploadedPosts[0]} />
              <BlogPostCard key={uploadedPosts[1].id} {...uploadedPosts[1]} />
              <CTANewsletter id={'blog-newsletter-cta'} isTextCard />
              <BlogPostCard key={uploadedPosts[2].id} {...uploadedPosts[2]} />
              {uploadedPosts.slice(3).map((blogPost) => (
                <BlogPostCard key={blogPost.id} {...blogPost} />
              ))}
            </Block>
          </div>

          {isMobile(breakpoints.small) && (
            // sticky sub navigation for mobile
            <CardsNavigation
              stickyLayoutOnBreakPoint
              categories={categories}
              hrefPrefix={'blog/category'}
              cssStyles={mobileSubNavStyle}
            />
          )}

          <div css={popularPostsStyle(blogBreakPoint)}>
            <p className={'heading4'}>Popular</p>
            <Divider size={1} isTop={false} notDecreased />
            <Block rowGap={1} alignContent={'stretch'} css={popularPostsBody(blogBreakPoint)}>
              {popularPosts.map((post) => {
                return <BlogPostCard {...post} isSmall key={post.id} />
              })}
              <div css={popularPostsBottom(blogBreakPoint)}></div>
            </Block>
          </div>
        </Block>

        {/* slide-up for search results (overlay)  */}
        <SearchResultsBlock
          search={search}
          debouncedSearch={debouncedSearch}
          resultsArray={filteredSearchPosts}
          resultsBlockContent={filteredSearchPosts.map((filteredPost) => (
            <BlogPostCard key={filteredPost.id} {...filteredPost} />
          ))}
          breakPoint={blogBreakPoint}
          cssStyles={blogSearchResultsBlockStyle}
        />
      </Body>
      <Footer />
    </Layout>
  )
}

export default BlogPage

export const query = graphql`
  query BlogPage {
    blogPosts: allContentfulBlogPost {
      edges {
        node {
          id
          slug
          title
          postCategory {
            name
            slug
            categoryColor
          }
          postAuthor {
            name
            slug
            authorPhoto {
              gatsbyImageData(width: 35, quality: 90)
            }
          }
          abstract {
            abstract
          }
          heroImage {
            gatsbyImageData(width: 800, quality: 90)
          }
          imageWithBorder
          createdAt
          publishedAt
        }
      }
    }

    popularPosts: allContentfulBlogPost(filter: { popular: { eq: true } }, sort: { popularOrder: ASC }) {
      edges {
        node {
          id
          slug
          title
          popular
          popularOrder
          postCategory {
            name
            slug
            categoryColor
          }
          postAuthor {
            name
            slug
            authorPhoto {
              gatsbyImageData(width: 35, quality: 90)
            }
          }
          createdAt
          publishedAt
        }
      }
    }

    blogCategories: allContentfulBlogCategory(
      sort: { order: ASC }
      filter: { id: { ne: "9605eb3f-012c-59ff-bcf5-37debd377762" } }
    ) {
      edges {
        node {
          name
          navigationIcon {
            title
            gatsbyImageData(width: 60, quality: 90)
          }
          categoryColor
          order
          slug
        }
      }
    }
  }
`

const mobileSubNavStyle = css`
  margin-bottom: 2rem;
`

const bottomContainerStyle = css`
  margin: 7rem 0 8rem;

  @media screen and (max-width: ${breakpoints.medium}px) {
    margin-bottom: 0;
  }
`

const topContainerStyle = css`
  margin-bottom: 6.5rem;

  .gatsby-image-wrapper {
    transition: all 0.3s ease-in-out;
  }

  &:hover {
    & > a > .gatsby-image-wrapper {
      transform: translateY(-8px);
      box-shadow: rgba(0, 0, 0, 0.05) 0 1rem 1rem 0;
    }
  }

  @media screen and (max-width: ${blogBreakPoint}px) {
    margin-top: 4rem;
  }

  @media screen and (max-width: ${breakpoints.medium}px) {
    padding-left: 0;
    padding-right: 0;
    margin-bottom: 3.5rem;
  }
`

const introHeroStyle = css`
  .subtitle {
    max-width: 47rem;
  }

  @media screen and (max-width: ${blogBreakPoint}px) {
    padding-bottom: 2rem;
  }
`

const bigImageStyle = css`
  .gatsby-image-wrapper {
    max-height: 25rem;
    border-radius: 0.5rem !important;
    border: none !important;
  }

  @media screen and (max-width: ${blogBreakPoint}px) {
    margin: auto;
  }

  @media screen and (max-width: ${breakpoints.small}px) {
    min-height: 10rem;
  }
`

const popularPostsStyle = (blogBreakPoint: number) => css`
  margin-left: 1rem;
  position: sticky;
  top: 5.5rem;

  .heading4 {
    margin-bottom: 0.25rem;
  }

  @media screen and (max-width: ${blogBreakPoint}px) {
    position: initial;
    background: #fafbfc;
    padding: 2rem;
    margin: 0 -2rem 4rem;
  }

  @media screen and (max-width: ${breakpoints.medium}px) {
    padding: 1.5rem;
    margin: 0 -1.5rem 4rem;
  }
`

const popularPostsBody = (blogBreakPoint: number) => css`
  // to stretch inner div to 100%
  & > div {
    display: flex;
  }

  @media screen and (min-width: ${blogBreakPoint}px) {
    max-height: calc(100vh - 7rem);
    overflow-y: scroll;

    --ms-overflow-style: none;

    &::-webkit-scrollbar {
      display: none;
    }

    /* for firefox */
    scrollbar-width: none;
  }

  @media screen and (max-width: ${blogBreakPoint}px) and (min-width: ${breakpoints.small + 1}px) {
    grid-template-columns: 1fr 1fr;
  }
`

const popularPostsBottom = (blogBreakPoint: number) => css`
  @media screen and (min-width: ${blogBreakPoint + 1}px) {
    padding-bottom: 30vh;
  }
`

const headingStyle = css`
  padding-top: 0.25rem;
`

const abstractStyle = css`
  margin: 0 0 1.5rem !important;
`

const firstPostDataStyle = css`
  display: flex;
  flex-direction: column;

  @media screen and (min-width: ${breakpoints.small + 1}px) and (max-width: ${blogBreakPoint}px) {
    align-items: center;
    margin: auto;
    max-width: 45rem;

    .heading2,
    .subtitle {
      text-align: center !important;
    }
  }
`

const blogSearchResultsBlockStyle = css`
  #search-results-reference > div > div {
    column-gap: 1.5rem;
  }
`

// export for BlogPost.tsx
export const firstPostLinkStyle = (categoryColor: string) => css`
  color: ${categoryColor};
`
